import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const Register = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    playerId: '',  // MongoDB _id des Players
    dsSpielerId: '', // Game-ID des Players  
    tribeId: '',    // MongoDB _id des Tribes
    world: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [availableWorlds, setAvailableWorlds] = useState([]);
  const [worldPlayers, setWorldPlayers] = useState([]);
  const [loadingPlayers, setLoadingPlayers] = useState(false);

  const { register } = useAuth();
  const navigate = useNavigate();

  // Verfügbare Welten beim Laden der Komponente abrufen
  useEffect(() => {
    const fetchAvailableWorlds = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/public/worlds/available`);
        if (response.data.success) {
          setAvailableWorlds(response.data.data.worlds);
        }
      } catch (error) {
        console.error('Fehler beim Laden der Welten:', error);
      }
    };

    fetchAvailableWorlds();
  }, []);

  // Spieler für ausgewählte Welt laden
  useEffect(() => {
    if (formData.world) {
      const fetchWorldPlayers = async () => {
        setLoadingPlayers(true);
        setWorldPlayers([]);
        try {
          const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/public/worlds/${formData.world}/players`);
          if (response.data.success) {
            setWorldPlayers(response.data.data.players);
          }
        } catch (error) {
          console.error('Fehler beim Laden der Spieler:', error);
          setError('Fehler beim Laden der Spieler für diese Welt');
        } finally {
          setLoadingPlayers(false);
        }
      };

      fetchWorldPlayers();
    } else {
      setWorldPlayers([]);
    }
  }, [formData.world]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Wenn Welt geändert wird, Spieler-Auswahl zurücksetzen
    if (name === 'world') {
      setFormData({
        ...formData,
        [name]: value,
        playerId: '',
        dsSpielerId: '',
        tribeId: ''
      });
    } else if (name === 'playerId') {
      // Wenn Spieler ausgewählt wird, alle Player-Daten setzen
      const selectedPlayer = worldPlayers.find(p => p._id === value);
      if (selectedPlayer) {
        setFormData({
          ...formData,
          playerId: selectedPlayer._id,
          dsSpielerId: selectedPlayer.playerId,
          tribeId: selectedPlayer.tribeId || ''
        });
      }
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwörter stimmen nicht überein');
      setLoading(false);
      return;
    }

    try {
      const result = await register({
        username: formData.username,
        password: formData.password,
        playerId: formData.playerId,
        dsSpielerId: formData.dsSpielerId,
        tribeId: formData.tribeId,
        world: formData.world
      });
      
      if (result.success) {
        navigate('/');
      } else {
        setError(result.message);
      }
    } catch (error) {
      setError('Ein unerwarteter Fehler ist aufgetreten');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        
        <div className="card">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-600 rounded-full shadow-lg mb-4">
              <span className="text-2xl text-white">👑</span>
            </div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              Registrierung
            </h1>
            <p className="text-gray-600">Erstelle dein Def-Tool Konto</p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
              <div className="font-medium">{error}</div>
            </div>
          )}

          {/* Register Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="form-label">
                Benutzername
              </label>
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                className="form-input"
                placeholder="Dein Benutzername"
                required
              />
            </div>

            <div>
              <label className="form-label">
                Welt auswählen
              </label>
              <select
                name="world"
                value={formData.world}
                onChange={handleChange}
                className="form-input"
                required
              >
                <option value="">-- Welt auswählen --</option>
                {availableWorlds.map((world) => (
                  <option key={world} value={world}>
                    {world}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="form-label">
                Spieler auswählen
              </label>
              <select
                name="playerId"
                value={formData.playerId}
                onChange={handleChange}
                className="form-input"
                disabled={!formData.world || loadingPlayers}
                required
              >
                <option value="">
                  {!formData.world 
                    ? "-- Erst Welt auswählen --" 
                    : loadingPlayers 
                      ? "Lade Spieler..." 
                      : "-- Spieler auswählen --"
                  }
                </option>
                {worldPlayers.map((player) => (
                  <option key={player._id} value={player._id}>
                    {player.name} ({player.playerId}) - {player.tribeName || 'Kein Stamm'}
                  </option>
                ))}
              </select>
              {loadingPlayers && (
                <p className="text-sm text-blue-600 mt-1">
                  Lade Spieler für {formData.world}...
                </p>
              )}
            </div>

            <div>
              <label className="form-label">
                Passwort
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="form-input"
                placeholder="Mindestens 6 Zeichen"
                required
                minLength="6"
              />
            </div>

            <div>
              <label className="form-label">
                Passwort bestätigen
              </label>
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                className="form-input"
                placeholder="Passwort wiederholen"
                required
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-success w-full"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <div className="spinner mr-2"></div>
                  Wird registriert...
                </span>
              ) : (
                'Registrieren'
              )}
            </button>
          </form>

          {/* Login Link */}
          <div className="mt-6 text-center">
            <p className="text-gray-600">
              Bereits ein Konto?{' '}
              <Link 
                to="/login" 
                className="text-blue-600 hover:text-blue-800 font-medium transition-colors"
              >
                Anmelden
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
